import React from 'react';
import { Box, Divider, IconButton, MenuItem, Modal, Select, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import UserAvatar from '../avatar/UserAvatar';
import { AdminTable } from '../adminTable';
import configuration from '../../config/Config';
import { formatMoney } from '../../utils/configUtils';
import StatusChip from '../statusChip/StatusChip';
import ReyLeaf from '../assests/reyleafLogo.svg';

const Field = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Heebo', mb: 0.5 }}>
      {label}
    </Typography>
    <Box sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', fontFamily: 'Heebo' }}>
      {children}
    </Box>
  </Box>
);

const PersonCard = ({ label, src, name }) => (
  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: '#F8F9FA', borderRadius: '16px' }}>
    <UserAvatar src={src} name={name} size={44} />
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Heebo' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Heebo', mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {name || '—'}
      </Typography>
    </Box>
  </Box>
);

/* ─── Product columns (shared) ────────────────────────────────────── */
const productColumns = [
  {
    field: 'photo', headerName: 'Image', width: 64,
    renderCell: (row) => (
      <img
        src={row?.product?.photos?.length > 0 ? `${configuration.cdnFront}/${row.product.photos[0].url}` : ReyLeaf}
        alt="product"
        style={{ height: 38, width: 38, borderRadius: '8px', objectFit: 'cover' }}
      />
    ),
  },
  { field: 'title',     headerName: 'Product',    minWidth: 130, renderCell: (r) => r?.product?.title || '—' },
  { field: 'category',  headerName: 'Category',   width: 110,    renderCell: (r) => r?.product?.categoryId?.name || '—' },
  { field: 'qty',       headerName: 'Qty',        width: 56,     renderCell: (r) => r?.quantity || '—' },
  { field: 'price',     headerName: 'Price',      width: 96,     renderCell: (r) => formatMoney(r?.product?.price) },
  { field: 'disc',      headerName: 'Discounted', width: 108,    renderCell: (r) => formatMoney(r?.product?.discountedProce) },
  { field: 'total',     headerName: 'Total',      width: 96,     renderCell: (r) => formatMoney(r?.quantity * (r?.product?.discountedProce || 0)) },
];

/* ─── Main component ──────────────────────────────────────────────── */
const OrderDetailModal = ({ open, onClose, order, items, status, onStatusChange }) => (
  <Modal open={open} onClose={onClose}>
    <Box sx={{
      position: 'absolute', top: '50%', left: '50%',
      transform: 'translate(-50%, -50%)',
      width: { xs: '95vw', sm: '88vw', md: '700px' },
      maxHeight: '88vh',
      bgcolor: '#fff',
      borderRadius: '24px',
      boxShadow: '0 24px 64px rgba(0,0,0,0.13)',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden', outline: 'none',
    }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#1a1a1a', fontFamily: 'Heebo', lineHeight: 1.1 }}>
            Order Details
          </Typography>
          {order?.orderId && (
            <Typography sx={{ fontSize: 12, color: '#9ca3af', fontFamily: 'Heebo', mt: 0.3 }}>
              #{order.orderId}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#6b7280', bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#ebebeb' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── Scrollable body ── */}
      <Box sx={{ overflowY: 'auto', px: 3, py: 2.5, flex: 1, '&::-webkit-scrollbar': { display: 'none' } }}>

        {order ? (
          <>
            {/* Buyer + Seller */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
              <PersonCard
                label="Buyer"
                src={order?.user?.picture ? `${configuration.cdnFront}/${order.user.picture}` : undefined}
                name={order?.user?.fullname}
              />
              <PersonCard
                label="Seller"
                src={order?.seller?.picture ? `${configuration.cdnFront}/${order.seller.picture}` : undefined}
                name={order?.seller?.fullname}
              />
            </Box>

            {/* Meta grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 2.5, mb: 3 }}>
              <Field label="Order Date">
                {order?.createdAt
                  ? new Date(order.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '—'}
              </Field>
              <Field label="Quantity">
                {order?.items?.reduce((t, i) => t + (i?.quantity || 0), 0) ?? '—'}
              </Field>
              <Field label="Total Amount">
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#065F46', fontFamily: 'Heebo' }}>
                  {formatMoney(order?.totalAmount)}
                </Typography>
              </Field>
              <Field label="Payment">
                <StatusChip value={order?.paymentStatus} />
              </Field>
              <Field label="Card">
                {order?.card || '—'}
              </Field>
              {!onStatusChange && (
                <Field label="Order Status">
                  <StatusChip value={order?.status} />
                </Field>
              )}
            </Box>

            {/* Shipping address */}
            {order?.address && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 3, p: 2, bgcolor: '#F8F9FA', borderRadius: '14px' }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 17, color: '#065F46', mt: 0.15, flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'Heebo', mb: 0.3 }}>
                    Shipping Address
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#362B24', fontFamily: 'Heebo', lineHeight: 1.6 }}>
                    {[order.address.address, order.address.city, order.address.state, order.address.country, order.address.zipcode].filter(Boolean).join(', ')}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Status selector — Orders.js only */}
            {onStatusChange && (
              <>
                <Divider sx={{ mb: 2.5 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#362B24', fontFamily: 'Heebo' }}>
                    Order Status
                  </Typography>
                  <Select
                    value={status || ''}
                    onChange={(e) => onStatusChange(e.target.value)}
                    size="small"
                    sx={{
                      borderRadius: '20px', fontFamily: 'Heebo', fontSize: 13, height: 40, minWidth: 170,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#065F46' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#065F46', borderWidth: 1 },
                    }}
                    MenuProps={{ PaperProps: { sx: { borderRadius: '14px', mt: 0.5, boxShadow: '0 8px 24px rgba(0,0,0,0.10)' } } }}
                  >
                    {['pending','accepted','declined','packed','dispatched','completed','cancelled'].map((s) => (
                      <MenuItem key={s} value={s} sx={{ fontSize: 13, fontFamily: 'Heebo', textTransform: 'capitalize' }}>{s}</MenuItem>
                    ))}
                  </Select>
                </Box>
              </>
            )}

            {/* Items table */}
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', fontFamily: 'Heebo', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Order Items
            </Typography>
            <AdminTable columns={productColumns} rows={items || []} rowKey="_id" emptyText="No items" />
          </>
        ) : (
          <Typography sx={{ color: '#9ca3af', fontFamily: 'Heebo', textAlign: 'center', py: 6 }}>
            Loading order…
          </Typography>
        )}
      </Box>
    </Box>
  </Modal>
);

export default OrderDetailModal;
